import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  disableHelpScoutBeacon,
  enableHelpScoutBeacon,
  setHelpScoutBeaconCompact,
} from '../../util/helpScoutBeacon';
import useHelpScoutBeaconSuppressed from '../../util/useHelpScoutBeaconSuppressed';

// Phones and tablets (below --viewportLarge): the booking panel is inline and
// its pink Book / Continue button sits near the bottom-right of the screen.
const NARROW_QUERY = '(max-width: 1023px)';
// Phones (below --viewportMedium).
const PHONE_QUERY = '(max-width: 767px)';
// Listing, booking (/l/...) and listener profile (/u/...) pages carry the
// booking CTAs. Keep the bubble off them on narrow screens.
const BOOKING_PATH_RE = /^\/(l|u)\//;

export const isBookingPath = pathname => BOOKING_PATH_RE.test(pathname || '');

// Checkout / book steps under /l/ (forms with a pay / continue button).
const CHECKOUT_PATH_RE = /^\/l\/(?:[^/]+\/)?[^/]+\/(checkout|book)\/?$/;
// Sam, Oct 6 2026: forms and session pages where the labelled pill covered
// fields / buttons on phones. These get the compact icon-only bubble.
const COMPACT_PATH_RE = /^\/(login|signup|confirm|recover-password|reset-password|verify-email|order|sale|account\/change-password|account\/payments|now|session)(\/|$)/;

export const isCheckoutPath = pathname => CHECKOUT_PATH_RE.test(pathname || '');
export const isCompactBeaconPath = pathname =>
  isCheckoutPath(pathname) || COMPACT_PATH_RE.test(pathname || '');

const useMediaQuery = query => {
  const [narrow, setNarrow] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      !!window.matchMedia(query).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const update = () => setNarrow(!!mql.matches);
    update();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(update);
      return () => mql.removeListener(update);
    }
    return undefined;
  }, [query]);
  return narrow;
};

/**
 * Client-only mount point for the Help Scout Beacon. Rendered once inside
 * <BrowserRouter> in ClientApp (never during SSR).
 */
const HelpScoutBeacon = () => {
  const location = useLocation();
  const firstPath = useRef(true);
  const narrow = useMediaQuery(NARROW_QUERY);
  const phone = useMediaQuery(PHONE_QUERY);
  const pathname = location.pathname;
  // Phones on forms / checkout / session pages: small icon-only bubble.
  const compact = phone && isCompactBeaconPath(pathname);

  // Never let the bubble sit on the booking panel / Book button on narrow
  // screens. Checkout / book on a phone uses the compact bubble instead.
  useHelpScoutBeaconSuppressed(
    narrow && isBookingPath(pathname) && !(phone && isCheckoutPath(pathname)),
    'booking-mobile'
  );

  useEffect(() => {
    setHelpScoutBeaconCompact(compact);
  }, [compact]);

  useEffect(() => {
    enableHelpScoutBeacon();
    return () => disableHelpScoutBeacon();
  }, []);

  // Let Beacon know about SPA page views (URL-based suggestions / messages).
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    if (typeof window !== 'undefined' && typeof window.Beacon === 'function') {
      try {
        window.Beacon('event', {
          type: 'page-viewed',
          url: window.location.href,
          title: document.title,
        });
      } catch (e) {
        // ignore
      }
    }
  }, [location.pathname]);

  return null;
};

/**
 * Renders nothing; hides the Beacon while mounted (and `active`).
 */
export const HelpScoutBeaconSuppressor = ({ active = true, reason = 'session' }) => {
  useHelpScoutBeaconSuppressed(active, reason);
  return null;
};

export default HelpScoutBeacon;
